export interface TextSelection {
  text: string;
  startIndex: number;
  endIndex: number;
  context: string;
}

/**
 * Get the current text selection from the browser
 */
export function getTextSelection(): TextSelection | null {
  const selection = window.getSelection();
  
  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);
  const text = range.toString().trim();
  
  if (!text) {
    return null;
  }

  // Get the selected text
  const selectedText = text;
  
  // Get the container element (RichTextDisplay content)
  const container = range.commonAncestorContainer;
  const containerElement = container.nodeType === Node.TEXT_NODE 
    ? container.parentElement 
    : container as Element;
  
  if (!containerElement) {
    return null;
  }

  // Get the full text content
  const fullText = containerElement.textContent || '';
  
  // Calculate approximate start and end indices
  const preCaretRange = range.cloneRange();
  preCaretRange.selectNodeContents(containerElement);
  preCaretRange.setEnd(range.startContainer, range.startOffset);
  const startIndex = preCaretRange.toString().length;
  
  const endIndex = startIndex + selectedText.length;
  
  // Get context (50 chars before and after)
  const contextStart = Math.max(0, startIndex - 50);
  const contextEnd = Math.min(fullText.length, endIndex + 50);
  const context = fullText.substring(contextStart, contextEnd);
  
  return {
    text: selectedText,
    startIndex,
    endIndex,
    context
  };
}

/**
 * Find text position using context (for recovering selections)
 */
export function findTextPosition(
  fullText: string, 
  context: string, 
  selectedText: string
): { start: number; end: number } | null {
  // Try to find the context in the full text
  const contextIndex = fullText.indexOf(context);
  
  if (contextIndex === -1) {
    return null;
  }
  
  // Try to find the selected text within the context
  const textIndex = context.indexOf(selectedText);
  
  if (textIndex === -1) {
    return null;
  }
  
  const start = contextIndex + textIndex;
  const end = start + selectedText.length;
  
  return { start, end };
}

/**
 * Create a highlight span element for selected text
 */
export function createHighlightSpan(
  text: string, 
  color: string = 'bg-yellow-200 dark:bg-yellow-900'
): HTMLSpanElement {
  const span = document.createElement('span');
  span.className = `inline-block ${color} px-1 rounded cursor-pointer hover:opacity-80 transition-opacity`;
  span.textContent = text;
  return span;
}

/**
 * Group comments into threads (parent → replies)
 */
export function groupCommentsByThread<T extends { id: string; parent_comment_id: string | null }>(
  comments: T[]
): { parent: T; replies: T[] }[] {
  const rootComments = comments.filter(c => !c.parent_comment_id);
  const replyMap = new Map<string, T[]>();
  
  // Group replies by parent ID
  comments
    .filter(c => c.parent_comment_id)
    .forEach(reply => {
      const parentId = reply.parent_comment_id!;
      if (!replyMap.has(parentId)) {
        replyMap.set(parentId, []);
      }
      replyMap.get(parentId)!.push(reply);
    });
  
  // Combine parents with their replies
  return rootComments.map(parent => ({
    parent,
    replies: replyMap.get(parent.id) || []
  }));
}

/**
 * Get user initials for avatar
 */
export function getUserInitials(name: string): string {
  const parts = name.split(' ');
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
